import { create } from 'zustand';
import { toast } from 'react-toastify';
import { parseTextractResponse } from '../helper/ocrUtils';

export interface LogbookTemplate {
  id: string;
  name: string;
  url: string;
}

export interface AnnotatedSection {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  imageData: string;
  backendAnnotationId?: string;
}

type SensorValue = string | { sensorId: string; sensor: string };
export interface BoundingBox {
  Width: number;
  Height: number;
  Left: number;
  Top: number;
}

export interface CellData {
  id: string;
  value: string | null;
  sensor: SensorValue | null;
  type: 'text' | 'autocomplete' | string;
  meta?: Record<string, unknown>;
  rowSpan: number;
  colSpan: number;
  isHeader?: boolean;
  geometry: { BoundingBox: BoundingBox };
  headers: { rowHeader: string; colHeader: string };
}

export type SpannedCell = { spanned: true; id: string };

export interface TableData {
  id: string;
  annotationId: string;
  title: string;
  grid: (CellData | SpannedCell | null)[][];
}

export interface CustomField {
  id: string;
  label: string;
  dataType: 'Text' | 'Numeric' | 'Date';
}

export interface OCRStore {
  selectedTemplate: LogbookTemplate | null;
  annotations: Record<string, AnnotatedSection>;
  tables: Record<string, TableData>;
  annotationIds: string[];
  tableIds: string[];
  selectedBlockId: string | null;
  showCoverage: Set<string>;
  customFields: CustomField[];

  addCustomField: (field: Omit<CustomField, 'id'>) => void;
  removeCustomField: (fieldId: string) => void;
  toggleCoverage: (annotationId: string, turnOn?: boolean) => void;
  toggleAllCoverage: (turnOn: boolean, allAnnotationIds: string[]) => void;
  setSelectedTemplate: (template: LogbookTemplate | null) => void;
  addAnnotation: (annotation: AnnotatedSection) => void;
  addTable: (tableData: TableData) => void;
  deleteAnnotationAndAssociatedTables: (annotationIdToDelete: string) => void;
  updateTableCell: (tableId: string, rowIndex: number, colIndex: number, value: unknown) => void;
  setSelectedBlockId: (blockId: string | null) => void;
  populateStateForEdit: (loaderData: any) => void;
  resetAll: () => void;
}

export const useOCRStore = create<OCRStore>((set, get) => ({
  selectedTemplate: null,
  annotations: {},
  tables: {},
  annotationIds: [],
  tableIds: [],
  selectedBlockId: null,
  showCoverage: new Set(),
  customFields: [],

  deleteAnnotationAndAssociatedTables: annotationIdToDelete =>
    set(state => {
      const { [annotationIdToDelete]: _, ...remainingAnnotations } = state.annotations;
      const newAnnotationIds = state.annotationIds.filter(id => id !== annotationIdToDelete);

      const tablesToDelete = Object.values(state.tables).filter(
        table => table.annotationId === annotationIdToDelete
      );
      const tableIdsToDelete = new Set(tablesToDelete.map(t => t.id));

      const remainingTables = Object.fromEntries(
        Object.entries(state.tables).filter(([id]) => !tableIdsToDelete.has(id))
      );
      const newTableIds = state.tableIds.filter(id => !tableIdsToDelete.has(id));

      return {
        annotations: remainingAnnotations,
        annotationIds: newAnnotationIds,
        tables: remainingTables,
        tableIds: newTableIds,
      };
    }),

  addCustomField: field =>
    set(state => ({
      customFields: [...state.customFields, { ...field, id: `custom-field-${Date.now()}` }],
    })),

  removeCustomField: fieldId =>
    set(state => ({
      customFields: state.customFields.filter(field => field.id !== fieldId),
    })),

  toggleCoverage: (annotationId, turnOn) =>
    set(state => {
      const newCoverageSet = new Set(state.showCoverage);
      const shouldTurnOn = turnOn ?? !newCoverageSet.has(annotationId);

      if (shouldTurnOn) {
        newCoverageSet.add(annotationId);
      } else {
        newCoverageSet.delete(annotationId);
      }
      return { showCoverage: newCoverageSet };
    }),

  toggleAllCoverage: (turnOn, allAnnotationIds) =>
    set(() => {
      if (turnOn) {
        return { showCoverage: new Set(allAnnotationIds) };
      } else {
        return { showCoverage: new Set() };
      }
    }),

  setSelectedTemplate: template =>
    set({
      selectedTemplate: template,
      annotations: {},
      tables: {},
      annotationIds: [],
      tableIds: [],
      selectedBlockId: null,
      customFields: [],
    }),

  addAnnotation: annotation =>
    set(state => ({
      annotations: { ...state.annotations, [annotation.id]: annotation },
      annotationIds: [...state.annotationIds, annotation.id],
    })),

  addTable: tableData =>
    set(state => ({
      tables: { ...state.tables, [tableData.id]: tableData },
      tableIds: [...state.tableIds, tableData.id],
    })),

  updateTableCell: (tableId, rowIndex, colIndex, value) =>
    set(state => {
      const table = state.tables[tableId];
      if (!table) return state;

      const newGrid = table.grid.map(row => [...row]);
      const cellToUpdate = newGrid[rowIndex]?.[colIndex];

      if (cellToUpdate && !('spanned' in cellToUpdate)) {
        newGrid[rowIndex][colIndex] = { ...cellToUpdate, sensor: (value || null) as SensorValue };
        
        return {
          tables: { ...state.tables, [tableId]: { ...table, grid: newGrid } },
        };
      }
      return state;
    }),

  setSelectedBlockId: blockId => set({ selectedBlockId: blockId }),

  populateStateForEdit: loaderData => {
    if (!loaderData?.data) {
      toast.error('Attempted to populate state with invalid loader data.');
      return;
    }

    const {
      logbookTemplateDetails,
      attachmentDoc,
      logbookConfiguration,
      annotations: apiAnnotations,
    } = loaderData.data;

    const selectedTemplate: LogbookTemplate = {
      id: logbookTemplateDetails.id,
      name: logbookTemplateDetails.name,
      url: attachmentDoc.attachmentLink,
    };

    const sensorMap = new Map<string, SensorValue>();
    const aiHeaderMap = new Map<string, { rowHeader: string; colHeader: string }>();

    logbookConfiguration.forEach((item: any) => {
      if (!item.isCustom) {
        if (item.sensor) {
          sensorMap.set(item.blockId, {
            sensorId: item.sensor.id,
            sensor: item.sensor.tag,
          });
        }
        if (item.aiContext) {
          aiHeaderMap.set(item.blockId, item.aiContext);
        }
      }
    });

    const newAnnotations: Record<string, AnnotatedSection> = {};
    const newTables: Record<string, TableData> = {};

    apiAnnotations.forEach((anno: any, index: number) => {
      const frontendAnnotationId = `annotation-edit-${anno.id || index}`;

      newAnnotations[frontendAnnotationId] = {
        id: frontendAnnotationId,
        imageData: anno.imageInput.url,
        backendAnnotationId: anno.annotationId,
        x: 0,
        y: 0,
        width: 0,
        height: 0,
      };

      const parsedGridObjects = parseTextractResponse(anno.textract.Blocks, aiHeaderMap);

      parsedGridObjects.forEach((gridObject, gridIndex) => {
        const grid = gridObject.grid;

        const gridWithSensors = grid.map(row =>
          row.map(cell => {
            if (cell && !('spanned' in cell) && sensorMap.has(cell.id)) {
              return { ...cell, sensor: sensorMap.get(cell.id)! };
            }
            return cell;
          })
        );

        const tableId = `${frontendAnnotationId}-table-${gridIndex}`;
        newTables[tableId] = {
          id: tableId,
          annotationId: frontendAnnotationId,
          title: `Annotation #${index + 1}`,
          grid: gridWithSensors,
        };
      });
    });

    const customFields = logbookConfiguration
      .filter((item: any) => item.isCustom)
      .map(
        (item: any): CustomField => ({
          id: item.blockId,
          label: item.cellText,
          dataType: item.valueType,
        })
      );

    set({
      selectedTemplate,
      annotations: newAnnotations,
      annotationIds: Object.keys(newAnnotations),
      tables: newTables,
      tableIds: Object.keys(newTables),
      customFields,
      selectedBlockId: null,
      showCoverage: new Set(),
    });
  },

  resetAll: () =>
    set({
      selectedTemplate: null,
      annotations: {},
      tables: {},
      annotationIds: [],
      tableIds: [],
      selectedBlockId: null,
      showCoverage: new Set(),
      customFields: [],
    }),
}));